import { useQuery } from 'react-query';
import {
	getInvoicePDF,
	getInvoices,
	getInvoicesProducts,
	getOverDueAmount,
	getRecentInvoices,
	getRecentPayments,
} from '@/services/invoice';
import type { FilterType } from '@/types/table';

export const useGetInvoices = ({
	page = 1,
	limit = 15,
	enabled = true,
	tab = '',
	columnsFilters = '[]',
	search = '',
	status = '',
	globalFilters = '[]',
	sort = '',
	dateRangeFilter = '',
	additionalFilters = '',
}: FilterType) => {
	return useQuery({
		queryKey: [
			'Invoices',
			page,
			limit,
			tab,
			columnsFilters,
			search,
			status,
			globalFilters,
			sort,
			dateRangeFilter,
			additionalFilters || '',
		],
		queryFn: () =>
			getInvoices(
				page,
				limit,
				tab,
				columnsFilters,
				search,
				status as string,
				globalFilters as string,
				sort as string,
				dateRangeFilter as string,
				additionalFilters as string,
			),
		enabled, // Replace 'true' with the appropriate condition if needed
		keepPreviousData: false, // Keep showing previous data while fetching new data
		// staleTime: 30000, // Consider data fresh for 30 seconds
	});
};

export const useGetInvoicesProducts = ({
	page = 1,
	limit = 15,
	enabled = true,
	globalFilters = '[]',
}: FilterType) => {
	return useQuery({
		queryKey: ['Invoices-Products', page, limit, globalFilters],
		queryFn: () => getInvoicesProducts(page, limit, globalFilters as string),
		enabled, // Replace 'true' with the appropriate condition if needed
		keepPreviousData: false, // Keep showing previous data while fetching new data
		// staleTime: 30000, // Consider data fresh for 30 seconds
	});
};

export const useGetInvoicePDF = (
	invoiceNumber: string,
	clinicQBOId: string,
	enabled = true,
) => {
	return useQuery({
		queryKey: ['InvoicePDF', invoiceNumber, clinicQBOId],
		queryFn: () => getInvoicePDF(`${invoiceNumber}|${clinicQBOId}`),
		enabled,
	});
};

export const useGetRecentPayments = ({
	page = 1,
	limit = 50,
	enabled = true,
	clinics = [],
}: { page?: number; limit?: number; enabled?: boolean; clinics?: string[] }) => {
	return useQuery({
		queryKey: ['RecentPayments', page, limit, clinics],
		queryFn: () => getRecentPayments(page, limit, clinics),
		enabled,
	});
};

export const useGetRecentInvoices = ({
	page = 1,
	limit = 50,
	enabled = true,
	clinics = [],
}: { page?: number; limit?: number; enabled?: boolean; clinics?: string[] }) => {
	return useQuery({
		queryKey: ['RecentInvoices', page, limit, clinics],
		queryFn: () => getRecentInvoices(page, limit, clinics),
		enabled,
	});
};

export const useGetOverDueAmount = ({
	page = 1,
	limit = 9999,
	enabled = true,
	clinics = [],
	dateRangeFilter = '',
}: { page?: number; limit?: number; enabled?: boolean; clinics?: string[], dateRangeFilter?: string }) => {
	return useQuery({
		queryKey: ['OverDueAmount', page, limit, clinics, dateRangeFilter],
		queryFn: () => getOverDueAmount(page, limit, clinics, dateRangeFilter),
		enabled,
	});
};